<template>
  <div class="retailer-search-location-fi">
    <retailer-search-input-fi
      :value="location ?? undefined"
      class="retailer-search-location-fi__input"
      :user-location-loading="userLocationLoading"
      :user-location-error="locationError"
      @input="searchPlaces"
      @search="selectPlace(places[0])"
      @clear="emit('location-changed', null)"
      @user-location="useUserLocation"
      @keyup-enter="selectPlace(places[0])"
    />

    <div
      v-if="showPlaces && places.length > 0"
      class="retailer-search-location-fi__search-results"
    >
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-for="place in places"
        :key="place.place_id"
        class="retailer-search-location-fi__search-results__item"
        data-test="retailer-search-location-item"
        @click="selectPlace(place)"
        v-html="highlight(place.description)"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script setup lang="ts">
import escapeStringRegexp from 'escape-string-regexp';
import { ref, watch } from 'vue';

import type { AutocompleteOptions, GeocodedLocation } from '@/googleHelpers';

import RetailerSearchInputFi from './retailer-search-input-fi/RetailerSearchInputFi.vue';

defineOptions({ name: 'RetailerSearchLocationFi' });

interface Place {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  types: string[];
}

const props = withDefaults(
  defineProps<{
    autocompleteOptions?: AutocompleteOptions;
    loc?: GeocodedLocation | null;
    locationError?: boolean;
  }>(),
  {
    autocompleteOptions: () => ({}),
    loc: null,
    locationError: false,
  },
);

const emit = defineEmits<{
  'location-changed': [payload: GeocodedLocation | null];
  'user-location-error': [payload: string];
}>();

const location = ref<string | null>(null);
const places = ref<Place[]>([]);
const showPlaces = ref(false);
const userLocationLoading = ref(false);
const activePlace = ref(false);
const googleMaps = { lat: 0, lng: 0 };

watch(
  () => props.loc,
  (val) => {
    location.value = val?.description || '';
  },
);

const getLatLong = (place: Place) => {
  try {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      { placeId: place.place_id },
      (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        if (status === window.google.maps.GeocoderStatus.OK && results) {
          googleMaps.lat = results[0].geometry.location.lat();
          googleMaps.lng = results[0].geometry.location.lng();
          emit('location-changed', {
            description: place.description,
            ...googleMaps,
          });
        }
      },
    );
  } catch (error) {
    console.error(`Error getting LatLon coords: ${error}`);
  }
};

const selectPlace = (place: Place) => {
  if (place) {
    activePlace.value = true;
    location.value = place.description;
    getLatLong(place);
    showPlaces.value = false;
  }
};

const searchPlaces = async (value: string | null) => {
  location.value = value;
  if (activePlace.value) {
    activePlace.value = false;
    return;
  }

  try {
    if (location.value && window.google && window.google.maps) {
      const { AutocompleteSuggestion } = (await google.maps.importLibrary('places')) as any;

      const request: Record<string, any> = {
        input: location.value,
      };

      if (props.autocompleteOptions?.componentRestrictions?.country) {
        request.includedRegionCodes = Array.isArray(
          props.autocompleteOptions.componentRestrictions.country,
        )
          ? props.autocompleteOptions.componentRestrictions.country
          : [props.autocompleteOptions.componentRestrictions.country];
      }

      if (props.autocompleteOptions?.bounds) {
        request.locationBias = props.autocompleteOptions.bounds;
      }

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      places.value = suggestions.map((suggestion: any) => {
        const placePrediction = suggestion.placePrediction;
        return {
          place_id: placePrediction.placeId,
          description: placePrediction.text.text,
          structured_formatting: {
            main_text:
              placePrediction.structuredFormat?.mainText?.text || placePrediction.text.text,
            secondary_text: placePrediction.structuredFormat?.secondaryText?.text || '',
          },
          types: placePrediction.types || [],
        };
      });
      showPlaces.value = true;
    } else {
      emit('location-changed', null);
      places.value = [];
      showPlaces.value = false;
    }
  } catch (error) {
    console.error('Error fetching autocomplete suggestions:', error);
    places.value = [];
    showPlaces.value = false;
  }
};

const highlight = (text: string) => {
  let highlighted = text;
  location.value?.split(/\s+/).forEach((fragment) => {
    if (!fragment) {
      return;
    }
    const locationRegexp = new RegExp(escapeStringRegexp(fragment), 'i');
    highlighted = highlighted.replace(
      locationRegexp,
      (match) => `<span class="fi-semibold">${match}</span>`,
    );
  });
  return highlighted;
};

const useUserLocation = () => {
  userLocationLoading.value = true;

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latlng = { lat: position.coords.latitude, lng: position.coords.longitude };
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { location: latlng },
          (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
            if (status === 'OK') {
              if (results?.[0]) {
                const { formatted_address, geometry } = results[0];
                const userLocation = {
                  location: formatted_address,
                  lat: geometry.location.lat(),
                  lng: geometry.location.lng(),
                };
                activePlace.value = true;
                location.value = userLocation.location;
                places.value = [];
                showPlaces.value = false;
                googleMaps.lat = userLocation.lat;
                googleMaps.lng = userLocation.lng;
                emit('location-changed', {
                  description: userLocation.location,
                  ...googleMaps,
                  userLocation: true,
                });
              } else {
                console.error('No results found');
              }
            } else {
              console.error(status);
            }
            userLocationLoading.value = false;
          },
        );
      },
      (error) => {
        userLocationLoading.value = false;
        emit('user-location-error', error.message);
        console.error(error.message);
      },
    );
  } else {
    userLocationLoading.value = false;
    emit('user-location-error', 'navigator geolocation not defined');
    console.error('navigator geolocation not defined');
  }
};
</script>

<style scoped src="./retailer-search-location-fi" />
